import CruciblePiece from './cruciblePiece';
import * as config from '../config.json';
import * as THREE from 'three';
import { GemsConfig } from '../config';
import { GLTF } from 'three/examples/jsm/loaders/GLTFLoader';
import Proton from 'three.proton.js';
import CrucibleLoader from '../crucibleLoader';
import PRNG from '../PRNG';

export class Gems extends CruciblePiece {
  protected _config: GemsConfig;
  protected _proton: any;

  protected _color: THREE.Color;
  protected _bottomCogs: THREE.Object3D[];
  protected _topCogs: THREE.Object3D[];
  protected _glowCogs: THREE.Object3D[];
  protected _glowMaterial: THREE.MeshStandardMaterial;

  protected _isAnimating: boolean;
  protected _clickAnimTime: number;
  protected _glowAnimTime: number;

  protected _finalPosTop: THREE.Vector3;
  protected _finalPosBot: THREE.Vector3;
  protected _finalPosGlow: THREE.Vector3;

  protected _selectedCogTop: THREE.Object3D;
  protected _selectedCogBot: THREE.Object3D;
  protected _selectedGlowNode: THREE.Object3D;
  protected _yPosTop: number;
  protected _yPosBot: number;
  protected _yPosGlow: number;

  protected _tier: number;

  constructor() {
    super();
    this._config = config.gems;
    this._proton = null;
    this._color = new THREE.Color(0xffffff);

    this._bottomCogs = [];
    this._topCogs = [];
    this._glowCogs = [];
    this._glowMaterial = new THREE.MeshStandardMaterial();

    this._isAnimating = false;
    this._clickAnimTime = 0;
    this._glowAnimTime = 0;
    this._tier = 0;

    this._finalPosTop = new THREE.Vector3();
    this._finalPosBot = new THREE.Vector3();
    this._finalPosGlow = new THREE.Vector3();

    this._yPosTop = 1.1;
    this._yPosBot = 0.78;
    this._yPosGlow = 1.3;

    this._selectedCogTop = new THREE.Object3D();
    this._selectedCogBot = new THREE.Object3D();
    this._selectedGlowNode = new THREE.Object3D();
  }

  setup(gltf: GLTF) {
    // Reset node references when setting up a new crucible
    this._bottomCogs = [];
    this._topCogs = [];
    this._glowCogs = [];
    this._tier = 0;
    this._clickAnimTime = 0;
    this._glowAnimTime = 0;

    // Proton might not be needed, but is included to avoid errors. TODO: only create this if needed
    this._proton = new Proton();

    // Reset group object with gltf contents
    this.group = gltf.scene;
    this.group.scale.set(2, 2, 2); // This should be removed with final models

    this.group.traverse((node: any) => {
      if (node.isMesh) {
        node.castShadow = true;
        node.receiveShadow = true;
        if (node.material.map) node.material.map.anisotropy = 16;

        if (
          node.material &&
          node.material.name === this._config.gemsMaterialName
        ) {
          node.material.color = this._color;
          node.material.emissive = this._color;
          node.material.emissiveIntensity = 3;
          node.material.needsUpdate = true;
        }

        this._config.topCogNodeNames.forEach((nodeName, index) => {
          if (node.name === nodeName) {
            this._topCogs[index] = node;
          }
        });

        this._config.bottomCogNodeNames.forEach((nodeName, index) => {
          if (node.name === nodeName) {
            this._bottomCogs[index] = node;
          }
        });

        this._config.glowNodeNames.forEach((nodeName, index) => {
          if (node.name === nodeName) {
            this._glowCogs[index] = node;
            this._glowMaterial = node.material;
          }
        });
      }
    });

    // Remove glow nodes and add again if Crucible is high level enough
    this._glowCogs.forEach((glowNode) => {
      glowNode.parent?.remove(glowNode);
    });
  }

  setColor(color: THREE.Color) {
    this._color = color;
  }

  update(
    crucibleLevel: number,
    crucibleTier: number,
    metadataColor: string,
    loader: CrucibleLoader,
    prng: PRNG,
    scene: THREE.Scene
  ) {
    this._tier = crucibleTier;

    if (crucibleTier >= 1) {
      // Add gems color to glow material before spawning the glow mesh
      this._glowMaterial.color = this._color;
      this._glowMaterial.emissive = this._color;
      this._glowMaterial.needsUpdate = true;

      // Spawn glow meshes
      this._glowCogs.forEach((glowNode, index) => {
        this.group.children[0].add(glowNode);

        // Add particle emitter if the Tier is high enough
        if (crucibleTier >= 2) {
          const emitterPosition = new THREE.Vector3();
          glowNode.getWorldPosition(emitterPosition);

          // Offset color to make it slightly lighter and more saturated
          const particlesColor = new THREE.Color(metadataColor).offsetHSL(
            0,
            0.3,
            0.3
          );
          this._proton.addEmitter(
            this._createGemGlowEmitter(
              prng,
              loader,
              particlesColor,
              emitterPosition
            )
          );
        }
      });

      this._proton.addRender(new Proton.SpriteRender(scene));
    }
  }

  animate(timeElapsed: number) {
    if (this._isAnimating) {
      this._pressDownAnimation(0.7, 0.4, timeElapsed);
    }

    this._topCogs.forEach((cog) => {
      cog.rotation.y += this._config.topCogRotationSpeed * timeElapsed;
    });
    this._bottomCogs.forEach((cog) => {
      cog.rotation.y -= this._config.bottomCogRotationSpeed * 2 * timeElapsed;
    });

    if (this._tier > 0) this._animateGemsGlow(this._tier, timeElapsed);
    if (this._tier >= 1) this._proton.update();
  }

  playOnClick(raycaster: THREE.Raycaster) {
    const intersects = raycaster.intersectObjects(this._topCogs, false);

    if (intersects.length > 0) {
      this._isAnimating = true;
      this._clickAnimTime = 0;

      const index = this._topCogs.indexOf(intersects[0].object);
      this._selectedCogBot = this._bottomCogs[index];
      this._selectedGlowNode = this._glowCogs[index];
      this._selectedCogTop = intersects[0].object;

      this._finalPosTop = new THREE.Vector3(
        this._selectedCogTop.position.x,
        this._yPosTop,
        this._selectedCogTop.position.z
      );
      this._finalPosBot = new THREE.Vector3(
        this._selectedCogBot.position.x,
        this._yPosBot,
        this._selectedCogBot.position.z
      );
      this._finalPosGlow = new THREE.Vector3(
        this._selectedGlowNode.position.x,
        this._yPosGlow,
        this._selectedGlowNode.position.z
      );
    }
  }

  _animateGemsGlow(range: number, timeElapsed: number) {
    this._glowAnimTime += timeElapsed / 2;

    this._glowCogs.forEach((glowNode) => {
      glowNode.scale.y = 1 + Math.abs(Math.sin(this._glowAnimTime) * range);
      // this._glowMaterial.emissiveIntensity =
      //   1.5 + Math.abs(Math.sin(this._glowAnimTime) * range);
    });
  }

  _pressDownAnimation(duration: number, yOffset: number, timeElapsed: number) {
    if (this._clickAnimTime < duration) {
      if (this._clickAnimTime === 0) {
        this._selectedCogTop.position.y -= yOffset;
        this._selectedCogBot.position.y -= yOffset * 1.25;
        this._selectedGlowNode.position.y -= yOffset;

        this._selectedCogTop.rotateY(60);
      }

      this._clickAnimTime += timeElapsed;

      this._selectedCogTop.position.lerp(
        this._finalPosTop,
        (this._clickAnimTime * 0.75) / (duration * 0.75)
      );

      this._selectedCogBot.position.lerp(
        this._finalPosBot,
        this._clickAnimTime / duration
      );

      this._selectedGlowNode.position.lerp(
        this._finalPosGlow,
        this._clickAnimTime / duration
      );

      this._selectedCogTop.rotation.y -= this._clickAnimTime * 0.25;
    } else {
      this._isAnimating = false;
    }
  }

  _lerp(start: number, end: number, amount: number) {
    return (1 - amount) * start + amount * end;
  }

  _createGemGlowEmitter(
    prng: PRNG,
    loader: CrucibleLoader,
    color: THREE.Color,
    position: THREE.Vector3
  ) {
    // Create sprite for effect
    const index = prng.randomInt(
      'levelup',
      config.crucibleLevelConfig.particleTextures.length
    );
    const spriteUrl = config.crucibleLevelConfig.particleTextures[index];
    const sprite = this._createSprite(loader, spriteUrl, color);

    // Create particle emitter
    const emitter = new Proton.Emitter();
    emitter.rate = new Proton.Rate(
      new Proton.Span(6, 12),
      new Proton.Span(0.1, 0.4)
    );

    emitter.addInitialize(new Proton.Mass(1));
    emitter.addInitialize(new Proton.Radius(0.4, 0.8));
    emitter.addInitialize(new Proton.Life(1, 5));
    emitter.addInitialize(new Proton.Body(sprite));
    emitter.addInitialize(new Proton.Position(new Proton.BoxZone(2, 0.2, 2)));

    emitter.addInitialize(
      new Proton.Velocity(0, new Proton.Vector3D(0, 1, 0), 90)
    );

    emitter.addBehaviour(new Proton.Rotate('random', 'random'));
    emitter.addBehaviour(new Proton.Gravity(-0.006));
    emitter.addBehaviour(new Proton.Scale(1, 0.5));
    emitter.addBehaviour(new Proton.Alpha(1, 0, Infinity, Proton.easeInQuart));

    emitter.p.x = position.x;
    emitter.p.y = position.y;
    emitter.p.z = position.z;
    emitter.emit();

    return emitter;
  }

  _createSprite(loader: CrucibleLoader, spriteUrl: string, color: THREE.Color) {
    const map = loader.textureLoader.load(spriteUrl);
    const material = new THREE.SpriteMaterial({
      map: map,
      color: color,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      fog: true
    });

    material.color.convertSRGBToLinear();

    return new THREE.Sprite(material);
  }
}
